"use client";

import React from "react";
import { Check, X, AlertTriangle } from "lucide-react";

type CellStatus = "positive" | "limited" | "negative";

type ComparisonRow = {
  feature: string;
  values: {
    label: string;
    status: CellStatus;
  }[];
};

const PLATFORMS = ["Readwise", "Matter", "Omnivore", "Tavlo"] as const;

const ROWS: ComparisonRow[] = [
  {
    feature: "Primary Input",
    values: [
      { label: "Books / Articles", status: "limited" },
      { label: "Articles", status: "limited" },
      { label: "Articles", status: "limited" },
      { label: "Social Media", status: "positive" },
    ],
  },
  {
    feature: "Video Support",
    values: [
      { label: "Minimal", status: "negative" },
      { label: "Partial", status: "limited" },
      { label: "Partial", status: "limited" },
      { label: "AI Summaries", status: "positive" },
    ],
  },
  {
    feature: "Social Structure",
    values: [
      { label: "Flattened", status: "limited" },
      { label: "Flattened", status: "limited" },
      { label: "None", status: "negative" },
      { label: "Native Layouts", status: "positive" },
    ],
  },
  {
    feature: "Public Sharing",
    values: [
      { label: "Limited", status: "limited" },
      { label: "Yes", status: "positive" },
      { label: "Yes", status: "positive" },
      { label: "Public Pages", status: "positive" },
    ],
  },
  {
    feature: "AI Insights",
    values: [
      { label: "Yes", status: "positive" },
      { label: "Yes", status: "positive" },
      { label: "No", status: "negative" },
      { label: "Deep Context", status: "positive" },
    ],
  },
];

function StatusIcon({ status }: { status: CellStatus }) {
  if (status === "positive") {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/15 shrink-0">
        <Check className="w-3 h-3 text-emerald-400" strokeWidth={3} />
      </span>
    );
  }
  if (status === "limited") {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/15 shrink-0">
        <AlertTriangle className="w-3 h-3 text-amber-400" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500/10 shrink-0">
      <X className="w-3 h-3 text-red-400/70" strokeWidth={3} />
    </span>
  );
}

function DesktopTable() {
  return (
    <div className="hidden md:block overflow-x-auto rounded-2xl border border-white/[0.06]">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-white/[0.03]">
            <th className="text-left py-4 px-6 text-[11px] font-bold uppercase tracking-[0.15em] text-white/30 border-b border-white/[0.06] w-[180px]">
              Feature
            </th>
            {PLATFORMS.map((platform) => (
              <th
                key={platform}
                className={`text-left py-4 px-6 text-[11px] font-bold uppercase tracking-[0.15em] border-b border-white/[0.06] ${
                  platform === "Tavlo"
                    ? "text-brand-1 bg-brand-1/[0.04]"
                    : "text-white/30"
                }`}
              >
                {platform}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row, rowIndex) => (
            <tr
              key={row.feature}
              className={`transition-colors hover:bg-white/[0.02] ${
                rowIndex < ROWS.length - 1 ? "border-b border-white/[0.04]" : ""
              }`}
            >
              <td className="py-4 px-6 text-[13px] font-semibold text-white/60">
                {row.feature}
              </td>
              {row.values.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`py-4 px-6 ${
                    cellIndex === 3 ? "bg-brand-1/[0.04]" : ""
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <StatusIcon status={cell.status} />
                    <span
                      className={`text-[13px] leading-tight ${
                        cellIndex === 3 && cell.status === "positive"
                          ? "text-white font-semibold"
                          : "text-zinc-400"
                      }`}
                    >
                      {cell.label}
                    </span>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MobileCards() {
  return (
    <div className="md:hidden space-y-3">
      {ROWS.map((row) => (
        <div
          key={row.feature}
          className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden"
        >
          <div className="px-5 py-3 bg-white/[0.03] border-b border-white/[0.04]">
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/40">
              {row.feature}
            </span>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {PLATFORMS.map((platform, i) => (
              <div
                key={platform}
                className={`flex items-center justify-between px-5 py-3 ${
                  platform === "Tavlo" ? "bg-brand-1/[0.04]" : ""
                }`}
              >
                <span
                  className={`text-[13px] font-medium ${
                    platform === "Tavlo" ? "text-brand-1" : "text-white/40"
                  }`}
                >
                  {platform}
                </span>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[13px] ${
                      platform === "Tavlo" &&
                      row.values[i].status === "positive"
                        ? "text-white font-semibold"
                        : "text-zinc-400"
                    }`}
                  >
                    {row.values[i].label}
                  </span>
                  <StatusIcon status={row.values[i].status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ComparisonTable() {
  return (
    <div className="my-16">
      <DesktopTable />
      <MobileCards />
    </div>
  );
}

export default ComparisonTable;
